import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useCart } from './CartContext';
import { normalizeCouponCode } from '../hooks/useCartQuote';

// The coupon code the customer APPLIED in the cart — their intent, nothing
// more. It is carried to Checkout so the checkout quote can submit the same
// code for the delivery address; whether it applies there, and what it takes
// off, is only ever that quote's answer. No quote, token or discount is kept.
//
// Lives in sessionStorage so it survives Cart → Checkout, a sign-in redirect
// and a reload, but not a new browser session. It is dropped when:
//   - the customer removes it,
//   - the cart becomes empty (an order was placed, or the cart was cleared),
//   - the customer signs out (or a different customer signs in).
export const COUPON_INTENT_STORAGE_KEY = 'fnh_coupon_intent';

const readStored = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(COUPON_INTENT_STORAGE_KEY));
    if (!parsed || parsed.schema !== 1) return { code: null, customerId: null };
    return { code: normalizeCouponCode(parsed.code), customerId: parsed.customerId || null };
  } catch {
    return { code: null, customerId: null };
  }
};

const writeStored = (intent) => {
  try {
    if (intent.code) {
      sessionStorage.setItem(COUPON_INTENT_STORAGE_KEY, JSON.stringify({ schema: 1, ...intent }));
    } else {
      sessionStorage.removeItem(COUPON_INTENT_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: the intent still lives for this page's lifetime.
  }
};

const CouponIntentContext = createContext(null);

export const CouponIntentProvider = ({ children }) => {
  const { authStatus, customer } = useAuth();
  const { items } = useCart();
  const [intent, setIntent] = useState(readStored);
  const customerId = customer?._id ? String(customer._id) : null;

  const update = useCallback((next) => {
    setIntent(next);
    writeStored(next);
  }, []);

  const setCouponCode = useCallback(
    (code) => update({ code: normalizeCouponCode(code), customerId }),
    [update, customerId]
  );
  const clearCouponCode = useCallback(() => update({ code: null, customerId: null }), [update]);

  // Cleanup rules. Each runs only on a real transition, so a stored code is
  // not dropped while the session or cart is still loading.
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (!intent.code) return;
    if (items.length === 0) clearCouponCode();
  }, [items.length, intent.code, clearCouponCode]);
  useEffect(() => {
    if (authStatus === 'authenticated') wasAuthenticated.current = true;
    if (!intent.code) return;
    const signedOut = authStatus !== 'authenticated' && authStatus !== 'loading';
    if (signedOut && wasAuthenticated.current) clearCouponCode();
    else if (customerId && intent.customerId && intent.customerId !== customerId) clearCouponCode();
  }, [authStatus, customerId, intent.code, intent.customerId, clearCouponCode]);

  const value = useMemo(
    () => ({ couponCode: intent.code, setCouponCode, clearCouponCode }),
    [intent.code, setCouponCode, clearCouponCode]
  );
  return <CouponIntentContext.Provider value={value}>{children}</CouponIntentContext.Provider>;
};

/**
 * The carried coupon intent. Without a provider (a page rendered on its own,
 * e.g. in a test) it falls back to page-local state, as Phase 7 had it.
 */
export const useCouponIntent = () => {
  const shared = useContext(CouponIntentContext);
  const [localCode, setLocalCode] = useState(null);
  const local = useMemo(
    () => ({
      couponCode: localCode,
      setCouponCode: (code) => setLocalCode(normalizeCouponCode(code)),
      clearCouponCode: () => setLocalCode(null),
    }),
    [localCode]
  );
  return shared || local;
};
